/**
 * collabBlobResolver — a tiny per-site registry that bridges the P2P blob
 * exchange (owned by useEditorDoc's collab effect) to the image path
 * (ImageWidget), without prop-drilling a BlobExchange through the widget tree.
 *
 * When free-mode collaboration is active, useEditorDoc registers a bridge keyed
 * by siteId with two hooks:
 *   - `get(sha)` — ImageWidget calls it on an OPFS miss BEFORE the cloud `/_s3`
 *     fallback: a peer that holds the image streams it back (sha-verified) into
 *     the SAME OPFS bucket the widget reads from.
 *   - `announce(sha)` — ImageWidget calls it right after a local upload so peers
 *     eagerly replicate the new image (D1 durability — it survives this tab
 *     leaving before anyone rendered it).
 *
 * Single module within @website-builder/editor (resolve.dedupe keeps editor a
 * singleton — see the grid-store dedupe note), so the Map is shared between the
 * effect and the widget.
 */
export type CollabBlobResolver = (sha: string) => Promise<Uint8Array | null>;

export interface CollabBlobBridge {
  readonly get: CollabBlobResolver;
  /** Announce a locally-added image so peers replicate it (D1). */
  readonly announce: (sha: string) => void;
  /** How many OTHER peers hold `sha` right now (durability-depth signal). */
  readonly probe: (sha: string) => Promise<number>;
}

const bridges = new Map<string, CollabBlobBridge>();

export function registerCollabBlobBridge(
  siteId: string,
  bridge: CollabBlobBridge,
): void {
  bridges.set(siteId, bridge);
}

export function unregisterCollabBlobBridge(siteId: string): void {
  bridges.delete(siteId);
}

/** The peer-fetch resolver for a site, or undefined when collab is inactive. */
export function getCollabBlobResolver(
  siteId: string,
): CollabBlobResolver | undefined {
  return bridges.get(siteId)?.get;
}

/** The replicate-announce hook for a site, or undefined when collab is inactive. */
export function getCollabBlobAnnounce(
  siteId: string,
): ((sha: string) => void) | undefined {
  return bridges.get(siteId)?.announce;
}

/** The holder-probe for a site, or undefined when collab is inactive (solo). */
export function getCollabHolderProbe(
  siteId: string,
): ((sha: string) => Promise<number>) | undefined {
  return bridges.get(siteId)?.probe;
}
